/**
 * Compliance store — backs the /compliance page.
 *
 * Holds the preset catalog (one fetch per session) + the most recent
 * scan report. The page can chain "list presets → pick one + a network
 * → run scan → drill in" with this store as the single source of truth.
 */
import { create } from "zustand";
import { get as apiGet, post as apiPost, patch as apiPatch, del as apiDel, ApiError } from "@/api/client";
import {
  CompliancePresetListSchema,
  ComplianceScanSummaryListSchema,
  ComplianceScanFullSchema,
  ComplianceScanDiffSchema,
  ComplianceProbeListSchema,
  DiagnosisResultSchema,
  PointerHistorySchema,
  type CompliancePreset,
  type ComplianceProbe,
  type ComplianceScanDiff,
  type ComplianceScanSummary,
  type DiagnosisResult,
  type PointerHistory,
} from "@/api/schemas/compliance";

interface State {
  presets: CompliancePreset[];
  presetsStatus: "idle" | "loading" | "ready" | "error";
  presetsError: string | null;

  /** Most recent scan, or null if no scan run yet this session. */
  report: DiagnosisResult | null;
  scanStatus: "idle" | "loading" | "ready" | "error";
  scanError: string | null;

  /** Recent scan history — drives the rail. */
  history: ComplianceScanSummary[];
  historyStatus: "idle" | "loading" | "ready" | "error";
  historyError: string | null;

  /** Result of the last manual probe-run; null otherwise. */
  probesRunResult: { ran: number; results: Array<{ presetSlug: string; networkId: string; status: string; reason?: string; summary?: unknown }> } | null;
  probesRunStatus: "idle" | "loading" | "ready" | "error";
  probesRunError: string | null;

  /** Persisted compliance probes (DB-backed; replaces the env var as the
   *  authoritative source). Drives the admin section of /compliance. */
  probes: ComplianceProbe[];
  probesStatus: "idle" | "loading" | "ready" | "error";
  probesError: string | null;

  /** Diff between two scans, when the diff page has been navigated to.
   *  Cleared when the user dismisses or navigates away. */
  diff: ComplianceScanDiff | null;
  diffStatus: "idle" | "loading" | "ready" | "error";
  diffError: string | null;

  /** Result of the last remediation queued from the UI (workflow run id +
   *  dryRun flag). Cleared on next remediation or when the user dismisses. */
  remediationResult: { id: string; dryRun: boolean } | null;
  remediationStatus: "idle" | "loading" | "ready" | "error";
  remediationError: string | null;

  loadPresets: () => Promise<void>;
  runScan: (args: { networkId: string; presetSlug: string }) => Promise<void>;
  clearReport: () => void;
  loadHistory: (args?: { networkId?: string; limit?: number }) => Promise<void>;
  /** Replay a saved scan as the active report (no upstream call). */
  loadScan: (scanId: string) => Promise<void>;
  /** Manually fire whatever's in OPUS_SCHEDULED_PROBES — admin only. */
  runProbesNow: () => Promise<void>;
  /** Fetch the per-pointer diff between two scans (a=newer, b=older). */
  loadDiff: (aId: string, bId: string) => Promise<void>;
  clearDiff: () => void;
  /** Queue a compliance remediation. Returns the run id; the caller
   *  redirects to the workflow runner page. dryRun=true is the preview
   *  path (no upstream write); false pushes live (subject to allowlist). */
  runRemediation: (args: { networkId: string; presetSlug: string; dryRun: boolean }) => Promise<string | null>;
  clearRemediation: () => void;

  /** Probe scheduling — admin page CRUD over the compliance_probes table. */
  loadProbes:    () => Promise<void>;
  addProbe:      (args: { presetSlug: string; networkId: string }) => Promise<boolean>;
  toggleProbe:   (probeId: string, enabled: boolean) => Promise<boolean>;
  removeProbe:   (probeId: string) => Promise<boolean>;

  /** Replace the tag list on a saved scan. Optimistically updates `report`
   *  if the active scan matches; rolls back on error. Also refreshes
   *  history so the rail rows pick up the new tag set. */
  setScanTags:   (scanId: string, tags: string[]) => Promise<boolean>;

  /** Per-pointer history — keyed by `${networkId}::${presetSlug}::${pointer}`.
   *  Map keeps multiple in-flight requests independent (an operator could
   *  expand history on several pointers in parallel). */
  pointerHistory: Record<string, { status: "idle" | "loading" | "ready" | "error"; data: PointerHistory | null; error: string | null }>;
  loadPointerHistory: (args: { networkId: string; presetSlug: string; pointer: string }) => Promise<void>;
}

export const useCompliance = create<State>((set, get) => ({
  presets: [],
  presetsStatus: "idle",
  presetsError: null,

  report: null,
  scanStatus: "idle",
  scanError: null,

  history: [],
  historyStatus: "idle",
  historyError: null,

  probesRunResult: null,
  probesRunStatus: "idle",
  probesRunError: null,

  diff: null,
  diffStatus: "idle",
  diffError: null,

  remediationResult: null,
  remediationStatus: "idle",
  remediationError: null,

  probes: [],
  probesStatus: "idle",
  probesError: null,

  pointerHistory: {},

  loadPresets: async () => {
    if (get().presetsStatus === "loading") return;
    set({ presetsStatus: "loading", presetsError: null });
    try {
      const raw = await apiGet<unknown>("/compliance/presets/");
      set({
        presets: CompliancePresetListSchema.parse(raw),
        presetsStatus: "ready",
      });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({ presetsStatus: "error", presetsError: msg });
    }
  },

  runScan: async ({ networkId, presetSlug }) => {
    if (get().scanStatus === "loading") return;
    set({ scanStatus: "loading", scanError: null });
    try {
      const raw = await apiPost<unknown>("/compliance/scan/", {
        network_id:  networkId,
        preset_slug: presetSlug,
      });
      set({
        report: DiagnosisResultSchema.parse(raw),
        scanStatus: "ready",
      });
      // After a successful scan, refresh the history rail so the user
      // sees their just-completed run land in the list.
      void get().loadHistory();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}: ${
              typeof err.data === "object" && err.data && "detail" in err.data
                ? String((err.data as { detail: unknown }).detail)
                : "scan failed"
            }`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({ scanStatus: "error", scanError: msg });
    }
  },

  clearReport: () => set({ report: null, scanStatus: "idle", scanError: null }),

  loadHistory: async ({ networkId, limit } = {}) => {
    if (get().historyStatus === "loading") return;
    set({ historyStatus: "loading", historyError: null });
    try {
      const raw = await apiGet<unknown>("/compliance/scans/", {
        network_id: networkId, limit,
      });
      set({
        history: ComplianceScanSummaryListSchema.parse(raw),
        historyStatus: "ready",
      });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({ historyStatus: "error", historyError: msg });
    }
  },

  loadScan: async (scanId) => {
    set({ scanStatus: "loading", scanError: null });
    try {
      const raw = await apiGet<unknown>(`/compliance/scans/${encodeURIComponent(scanId)}`);
      const full = ComplianceScanFullSchema.parse(raw);
      // Reshape the saved-scan row into the DiagnosisResult shape the page
      // already knows how to render. The fields line up except for `target`
      // (we use networkId + networkName).
      const report: DiagnosisResult = {
        process:    full.processName,
        presetSlug: full.presetSlug,
        target:     full.networkName ? `${full.networkName} (${full.networkId})` : full.networkId,
        summary:    full.summary,
        results:    full.results,
        scanId:     full.id,
        durationMs: full.durationMs ?? undefined,
        tags:       full.tags,
      };
      set({ report, scanStatus: "ready" });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({ scanStatus: "error", scanError: msg });
    }
  },

  loadDiff: async (aId, bId) => {
    if (get().diffStatus === "loading") return;
    set({ diffStatus: "loading", diffError: null });
    try {
      const raw = await apiGet<unknown>(
        `/compliance/scans/${encodeURIComponent(aId)}/diff/${encodeURIComponent(bId)}`,
      );
      set({ diff: ComplianceScanDiffSchema.parse(raw), diffStatus: "ready" });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({ diffStatus: "error", diffError: msg });
    }
  },

  clearDiff: () => set({ diff: null, diffStatus: "idle", diffError: null }),

  runRemediation: async ({ networkId, presetSlug, dryRun }) => {
    if (get().remediationStatus === "loading") return null;
    set({ remediationStatus: "loading", remediationError: null, remediationResult: null });
    try {
      // Backend re-validates remediability + allowlist; we send the raw
      // intent and surface whatever 400/403 it raises.
      const raw = await apiPost<{ id: string; state: string; dryRun: boolean }>(
        "/compliance/remediate/",
        { network_id: networkId, preset_slug: presetSlug, dry_run: dryRun },
      );
      set({
        remediationResult: { id: raw.id, dryRun: raw.dryRun },
        remediationStatus: "ready",
      });
      return raw.id;
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}: ${
              typeof err.data === "object" && err.data && "detail" in err.data
                ? String((err.data as { detail: unknown }).detail)
                : "remediation queue failed"
            }`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({ remediationStatus: "error", remediationError: msg });
      return null;
    }
  },

  clearRemediation: () => set({
    remediationResult: null, remediationStatus: "idle", remediationError: null,
  }),

  loadProbes: async () => {
    if (get().probesStatus === "loading") return;
    set({ probesStatus: "loading", probesError: null });
    try {
      const raw = await apiGet<unknown>("/compliance/probes/");
      set({
        probes: ComplianceProbeListSchema.parse(raw),
        probesStatus: "ready",
      });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error ? err.message : "unknown error";
      set({ probesStatus: "error", probesError: msg });
    }
  },

  addProbe: async ({ presetSlug, networkId }) => {
    try {
      await apiPost("/compliance/probes/", {
        preset_slug: presetSlug, network_id: networkId,
      });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}: ${
              typeof err.data === "object" && err.data && "detail" in err.data
                ? String((err.data as { detail: unknown }).detail)
                : "add probe failed"
            }`
          : err instanceof Error ? err.message : "unknown error";
      set({ probesError: msg });
      return false;
    }
    await get().loadProbes();
    return true;
  },

  toggleProbe: async (probeId, enabled) => {
    try {
      await apiPatch(`/compliance/probes/${encodeURIComponent(probeId)}`, { enabled });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error ? err.message : "unknown error";
      set({ probesError: msg });
      return false;
    }
    await get().loadProbes();
    return true;
  },

  removeProbe: async (probeId) => {
    try {
      await apiDel(`/compliance/probes/${encodeURIComponent(probeId)}`);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error ? err.message : "unknown error";
      set({ probesError: msg });
      return false;
    }
    await get().loadProbes();
    return true;
  },

  loadPointerHistory: async ({ networkId, presetSlug, pointer }) => {
    const key = `${networkId}::${presetSlug}::${pointer}`;
    const cur = get().pointerHistory[key];
    if (cur && cur.status === "loading") return;
    set({
      pointerHistory: {
        ...get().pointerHistory,
        [key]: { status: "loading", data: cur?.data ?? null, error: null },
      },
    });
    try {
      const raw = await apiGet<unknown>("/compliance/pointers/history", {
        network_id:  networkId,
        preset_slug: presetSlug,
        pointer,
      });
      const data = PointerHistorySchema.parse(raw);
      set({
        pointerHistory: {
          ...get().pointerHistory,
          [key]: { status: "ready", data, error: null },
        },
      });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error ? err.message : "unknown error";
      set({
        pointerHistory: {
          ...get().pointerHistory,
          [key]: { status: "error", data: null, error: msg },
        },
      });
    }
  },

  setScanTags: async (scanId, tags) => {
    let resp: { id: string; tags: string[] };
    try {
      resp = await apiPatch<{ id: string; tags: string[] }>(
        `/compliance/scans/${encodeURIComponent(scanId)}`,
        { tags },
      );
    } catch {
      return false;
    }
    // Reflect on the active report immediately (optimistic), and refresh
    // the rail so the new tag set lands on the row too.
    const cur = get().report;
    if (cur && cur.scanId === scanId) {
      set({ report: { ...cur, tags: resp.tags } });
    }
    void get().loadHistory();
    return true;
  },

  runProbesNow: async () => {
    if (get().probesRunStatus === "loading") return;
    set({ probesRunStatus: "loading", probesRunError: null, probesRunResult: null });
    try {
      const raw = await apiPost<{ ran: number; results: Array<{ presetSlug: string; networkId: string; status: string; reason?: string; summary?: unknown }>; note?: string }>("/compliance/probes/run-now/");
      set({ probesRunResult: raw, probesRunStatus: "ready" });
      // Persist runs landed in compliance_scans, so refresh the rail.
      void get().loadHistory();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({ probesRunStatus: "error", probesRunError: msg });
    }
  },
}));
